// Copyright ©️ 2026 eVolve MEP, LLC

import { useSyncExternalStore } from 'react';

import { Text } from '@mantine/core';

import type {
  RenderedRackLabel,
  RenderedRackLabelsStore,
} from 'components/Autodesk/extensions/DimensioningExtension/RackDimensions/rendering/createRenderedRackLabelsStore';

type LabelProps = { label: RenderedRackLabel };

/** A single measurement drawn over the viewer canvas, haloed so it stays readable against the model. */
const RackDimensionLabel = ({ label }: LabelProps) => (
  <Text
    component="div"
    pos="absolute"
    fw={600}
    c="black"
    style={{
      textShadow: '0 0 3px white, 0 0 2px white',
      top: `${label.y}px`,
      left: `${label.x}px`,
      transform: 'translate(-50%, -50%)',
      pointerEvents: 'none',
      whiteSpace: 'nowrap',
    }}
  >
    {label.text}
  </Text>
);

type Props = { renderedRackLabelsStore: RenderedRackLabelsStore };

/**
 * Renders the measurement labels for the automatic conduit-rack dimensions. These update on every camera
 * change, so keep this as low in the component tree as possible.
 */
export const RackDimensionLabelsOverlay = ({ renderedRackLabelsStore }: Props) => {
  const labels = useSyncExternalStore(renderedRackLabelsStore.subscribe, renderedRackLabelsStore.getSnapshot);

  return labels.map((label) =>
    label.visible ? <RackDimensionLabel key={label.rackDimensionLabelId} label={label} /> : null,
  );
};
